import isNil from 'lodash/isNil'
import merge from 'lodash/merge'
import pickBy from 'lodash/pickBy'
import isString from 'lodash/isString'
import { VALID_COUNTRY_CODES, type CountryCode } from '@klear/constants/countries'

export type AddressJson = {
  country: CountryCode
  line1?: string
  line2?: string
  city?: string
  state?: string
  postalCode?: string
}

export function addressToJson(address: Address): AddressJson {
  return pickAddressFields({
    country: address.country,
    line1: address.line1,
    line2: address.line2,
    city: address.city,
    state: address.state,
    postalCode: address.postalCode
  }) as AddressJson
}

function canonicalString(value: string | null | undefined): string | undefined {
  if (!isString(value)) return undefined
  const trimmed = value.trim()
  return trimmed === '' ? undefined : trimmed
}

function pickAddressFields<T extends Record<string, unknown>>(record: T): Partial<T> {
  return pickBy(record, (value) => !isNil(value) && !(isString(value) && value === '')) as Partial<T>
}

function canonicalCountry(value: unknown): CountryCode | undefined {
  if (!isString(value)) return undefined
  const upper = value.trim().toUpperCase() as CountryCode
  return VALID_COUNTRY_CODES.has(upper) ? upper : undefined
}

function canonicalAddress(input: AddressJson) {
  const country = canonicalCountry(input.country)
  if (!country) {
    throw new Error(`Invalid Address country: ${input.country}`)
  }

  return pickAddressFields({
    country,
    line1: canonicalString(input.line1),
    line2: canonicalString(input.line2),
    city: canonicalString(input.city),
    state: canonicalString(input.state),
    postalCode: canonicalString(input.postalCode)
  }) as AddressJson
}

export class Address {
  static readonly __klearClass = 'Address'

  readonly country: CountryCode
  readonly line1?: string
  readonly line2?: string
  readonly city?: string
  readonly state?: string
  readonly postalCode?: string

  constructor(input: AddressJson) {
    const address = canonicalAddress(input)
    this.country = address.country
    this.line1 = address.line1
    this.line2 = address.line2
    this.city = address.city
    this.state = address.state
    this.postalCode = address.postalCode
  }

  toString(): string {
    return [this.line1, this.line2, this.city, this.state, this.postalCode, this.country].filter(Boolean).join(', ')
  }

  toJSON(): AddressJson {
    return addressToJson(this)
  }

  with(patch: Partial<AddressJson>): Address {
    return new Address(merge({}, addressToJson(this), patch))
  }

  withCountry(country: CountryCode): Address {
    return this.with({ country })
  }
}
